import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Skeleton } from 'antd';
import {
  AimOutlined,
  TrophyOutlined,
  CalendarOutlined,
  WarningOutlined,
  HistoryOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  RightOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { dashboardApi } from '@/api/dashboardApi';
import { activityApi } from '@/api/activityApi';
import { actionItemApi, ActionItem } from '@/api/actionItemApi';
import { DashboardSummary, PipelineViewData, RfoCard, ActivityEvent } from '@/types/rfo';
import {
  PHASE_ORDER,
  getGateLetter,
  getPhaseColor,
  getPhaseLabel,
  getPhaseShortLabel,
  getPhaseOwnerRole,
} from '@/utils/phaseHelpers';
import { isOverdue as isDateOverdue, daysUntil, formatRelativeTime } from '@/utils/dateFormatters';
import { useAuth } from '@/auth/useAuth';

import '@/styles/dashboard.css';

const { Text } = Typography;

// Recent-activity action -> chip label + color. Backend ActivityDto.action values
// are raw audit verbs (CREATED, PHASE_ADVANCED, DELETED, ...); unknown values fall
// back to a title-cased label.
const ACTION_META: Record<string, { label: string; color: string }> = {
  CREATED: { label: 'Created', color: '#10b981' },
  UPDATED: { label: 'Updated', color: '#3b82f6' },
  DELETED: { label: 'Deleted', color: '#ef4444' },
  PHASE_ADVANCED: { label: 'Advanced', color: '#6c5ce7' },
  PHASE_TRANSITION: { label: 'Advanced', color: '#6c5ce7' },
  PHASE_ROLLBACK: { label: 'Rolled back', color: '#f97316' },
  PHASE_MOVED: { label: 'Moved', color: '#6c5ce7' },
  WORKFLOW_INITIALIZED: { label: 'Workflow', color: '#06b6d4' },
  WORKFLOW_HELD: { label: 'On hold', color: '#f59e0b' },
  WORKFLOW_CANCELLED: { label: 'Cancelled', color: '#ef4444' },
  BID_DECISION: { label: 'Decision', color: '#3b82f6' },
  OPPORTUNITY_CREATE: { label: 'Created', color: '#10b981' },
  DOCUMENT_UPLOAD: { label: 'Upload', color: '#74b9ff' },
  DOCUMENT_UPLOADED: { label: 'Upload', color: '#74b9ff' },
  CONTENT_UPDATE: { label: 'Content', color: '#06b6d4' },
  REVIEW_COMPLETE: { label: 'Review', color: '#f59e0b' },
  EXECUTIVE_SIGNOFF: { label: 'Sign-off', color: '#f97316' },
  ASSIGNMENT_CHANGE: { label: 'Assignment', color: '#fd79a8' },
};

const ENTITY_VERB: Record<string, Record<string, string>> = {
  OPPORTUNITY: { CREATED: 'created an opportunity', UPDATED: 'updated an opportunity', DELETED: 'deleted an opportunity' },
  WORKFLOW: { PHASE_ADVANCED: 'advanced a phase', PHASE_ROLLBACK: 'rolled back a phase', PHASE_MOVED: 'moved a phase' },
};

// Turn the audit `details` payload (a JSON {method, class} blob in the live API)
// into a short human sentence; prefer the entity/action verb map, else the raw text.
const describeActivity = (a: ActivityEvent): string => {
  const byEntity = ENTITY_VERB[a.entityType];
  if (byEntity && byEntity[a.action]) return byEntity[a.action];
  const raw = a.details;
  if (raw && typeof raw === 'string' && !raw.trim().startsWith('{')) return raw;
  const meta = ACTION_META[a.action];
  return meta ? meta.label.toLowerCase() : String(a.action || '').toLowerCase().replace(/_/g, ' ');
};

// Stable avatar palette — deterministic from the name so each person keeps one color.
const AVATAR_COLORS = [
  '#6c5ce7', '#3b82f6', '#06b6d4', '#10b981',
  '#f59e0b', '#ec4899', '#f97316', '#0ea5e9', '#a855f7',
];

const colorFor = (name?: string): string => {
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initialsFor = (name?: string): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const firstNameOf = (name?: string): string =>
  String(name || '').trim().split(/\s+/)[0] || '';

const NameAvatar: React.FC<{ name?: string; size?: number }> = ({ name, size = 28 }) => (
  <span
    className="dashboard-name-avatar"
    style={{ backgroundColor: colorFor(name), width: size, height: size }}
    title={name}
  >
    {initialsFor(name)}
  </span>
);

// "3d phase overdue" / "Due today" / "Due in 5d" from the card's deadline fields.
const cardDueText = (card: RfoCard): string => {
  const phaseOverdue = card.phaseDeadline && isDateOverdue(card.phaseDeadline);
  const submissionOverdue = card.dueDate && isDateOverdue(card.dueDate);

  if (phaseOverdue) {
    const d = Math.abs(daysUntil(card.phaseDeadline!));
    return `${d}d phase overdue`;
  }
  if (submissionOverdue) {
    const d = Math.abs(daysUntil(card.dueDate!));
    return `${d}d submission overdue`;
  }

  const days = card.daysUntilDeadline;
  if (typeof days === 'number') {
    if (days === 0) return 'Due today';
    return `Due in ${days}d`;
  }
  const due = card.phaseDeadline || card.dueDate;
  if (!due) return '';
  const d = daysUntil(due);
  if (d === 0) return 'Due today';
  return `Due in ${d}d`;
};

type CardSeverity = 'overdue' | 'soon' | 'ok';
const cardSeverity = (card: RfoCard): CardSeverity => {
  const due = card.phaseDeadline || card.dueDate;
  if (card.deadlineOverdue || (due && isDateOverdue(due))) return 'overdue';
  const days = typeof card.daysUntilDeadline === 'number'
    ? card.daysUntilDeadline
    : due ? daysUntil(due) : null;
  if (days !== null && days <= 7) return 'soon';
  return 'ok';
};

// Card title with the icon wrapped in a tinted rounded chip.
const CardTitle: React.FC<{
  icon: React.ReactNode;
  color?: string;
  children: React.ReactNode;
}> = ({ icon, color = '#2563EB', children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
    <span
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `${color}14`,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 15,
      }}
    >
      {icon}
    </span>
    {children}
  </span>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [pipeline, setPipeline] = useState<PipelineViewData | null>(null);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [activityError, setActivityError] = useState(false);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionItemsError, setActionItemsError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryData, pipelineData] = await Promise.all([
        dashboardApi.getSummary(),
        dashboardApi.getPipeline(),
      ]);
      setSummary(summaryData);
      setPipeline(pipelineData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
    // Secondary widgets — failures here shouldn't blank the dashboard.
    activityApi
      .getRecentActivity(8)
      .then((events) => setActivity(Array.isArray(events) ? events : []))
      .catch(() => setActivityError(true));
    // Team workload needs ALL assignees — the board endpoint (ADMIN/CEO) has them;
    // fall back to the current-user list for other roles.
    actionItemApi
      .getBoard()
      .catch(() => actionItemApi.getAll())
      .then((items) => setActionItems(Array.isArray(items) ? items : []))
      .catch(() => setActionItemsError(true));
  };

  // Compute derived data
  const firstName = user?.name?.split(' ')[0] || user?.preferred_username || 'there';

  const winRate =
    summary && (summary.wonOpportunities + summary.lostOpportunities) > 0
      ? Math.round(
          (summary.wonOpportunities / (summary.wonOpportunities + summary.lostOpportunities)) * 100,
        )
      : 0;

  const allCards: RfoCard[] = pipeline
    ? Object.values(pipeline.pipelineByPhase).flat()
    : [];

  // Count RFOs due within the next 7 calendar days (includes today, excludes past).
  const dueThisWeek = allCards.filter((card) => {
    const due = card.phaseDeadline || card.dueDate;
    if (!due) return false;
    const d = daysUntil(due);
    return d >= 0 && d <= 7;
  }).length;

  const overdueCards = allCards.filter((c) => cardSeverity(c) === 'overdue');
  const dueSoonCards = allCards.filter((c) => cardSeverity(c) === 'soon');
  const overdueCount = overdueCards.length;
  const phaseOverdueCount = allCards.filter((c) => c.phaseDeadline && isDateOverdue(c.phaseDeadline)).length;
  const submissionOverdueCount = allCards.filter((c) => c.dueDate && isDateOverdue(c.dueDate)).length;
  const attentionCards = [...overdueCards, ...dueSoonCards].slice(0, 6);

  // Upcoming deadlines: next 5 by phase deadline (falls back to due date).
  const upcomingCards = [...allCards]
    .filter((c) => c.phaseDeadline || c.dueDate)
    .sort((a, b) =>
      dayjs(a.phaseDeadline || a.dueDate).valueOf() - dayjs(b.phaseDeadline || b.dueDate).valueOf(),
    )
    .slice(0, 5);

  // Team workload: derive owners from action-item assignees (pipeline cards carry
  // no owner). Gracefully empty when the backend hasn't assigned anyone yet.
  const ownerCounts: Record<string, number> = {};
  actionItems.forEach((it) => {
    if (!it.assigneeName) return;
    ownerCounts[it.assigneeName] = (ownerCounts[it.assigneeName] || 0) + 1;
  });
  const workload = Object.entries(ownerCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  const maxLoad = Math.max(1, ...workload.map((w) => w.count));

  // Pipeline Flow: per-gate counts in pipeline order.
  const pipelineFlow = PHASE_ORDER.map((phase) => ({
    phase,
    count: (pipeline?.pipelineByPhase?.[phase] || []).length,
  }));
  const openGatesCount = pipelineFlow.filter((p) => p.count > 0).length;
  const wonCount = summary?.wonOpportunities ?? 0;
  const lostCount = summary?.lostOpportunities ?? 0;

  if (loading) {
    return (
      <div className="dashboard-page">
        {/* Skeleton greeting */}
        <Skeleton.Input active style={{ width: 300, height: 28, marginBottom: 24 }} />

        {/* Skeleton stat cards */}
        <Row gutter={[16, 16]} className="dashboard-skeleton-row">
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card style={{ borderRadius: 12 }}>
                <Skeleton active paragraph={{ rows: 1 }} />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Skeleton charts */}
        <Row gutter={[16, 16]} className="dashboard-skeleton-row">
          <Col xs={24} lg={14}>
            <Card style={{ borderRadius: 12, height: 340 }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card style={{ borderRadius: 12, height: 340 }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Col>
        </Row>

        {/* Skeleton pipeline */}
        <Card style={{ borderRadius: 12, marginBottom: 24 }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-content">
      {/* Welcome banner */}
      <div className="dashboard-banner">
        <div className="dashboard-banner-orb" />
        <div className="dashboard-banner-inner">
          <div className="dashboard-banner-left">
            <NameAvatar name={user?.name || firstName} size={52} />
            <div>
              <h1 className="dashboard-banner-title">Welcome back, {firstName}</h1>
              <p className="dashboard-banner-sub">
                You have <strong>{summary?.activeOpportunities ?? 0} active pursuits</strong>
                <span className="dashboard-banner-dot">·</span>
                <span className="dashboard-banner-overdue">{overdueCount} past deadline</span>
                <span className="dashboard-banner-dot">·</span>
                <span className="dashboard-banner-winrate">{winRate}% win rate</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="dashboard-banner-cta"
            onClick={() => navigate('/opportunities/new')}
          >
            <PlusOutlined /> New Opportunity
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-kpi-grid">
        {[
          {
            label: 'Active Pursuits',
            value: summary?.activeOpportunities ?? 0,
            sub: `across ${openGatesCount} open gate${openGatesCount === 1 ? '' : 's'}`,
            color: '#8B5CF6',
            icon: <AimOutlined />,
          },
          {
            label: 'Due This Week',
            value: dueThisWeek,
            sub: 'deadline approaching',
            color: '#3B82F6',
            icon: <CalendarOutlined />,
          },
          {
            label: 'Past Deadline',
            value: overdueCount,
            sub: overdueCount > 0 ? `${phaseOverdueCount} phase · ${submissionOverdueCount} submission` : 'all on track',
            color: '#EF4444',
            icon: <WarningOutlined />,
          },
          {
            label: 'Win Rate',
            value: `${winRate}%`,
            sub: `${wonCount} won · ${lostCount} lost`,
            color: '#10B981',
            icon: <TrophyOutlined />,
          },
        ].map((k) => (
          <div key={k.label} className="dashboard-kpi-card">
            <div className="dashboard-kpi-top">
              <div>
                <span className="dashboard-kpi-label">{k.label}</span>
                <span className="dashboard-kpi-value">{k.value}</span>
              </div>
              <span
                className="dashboard-kpi-icon"
                style={{ background: `${k.color}1a`, color: k.color }}
              >
                {k.icon}
              </span>
            </div>
            <span className="dashboard-kpi-sub">{k.sub}</span>
            <span className="dashboard-kpi-accent" style={{ background: k.color }} />
          </div>
        ))}
      </div>

      {/* Pipeline Flow */}
      <Card
        className="dashboard-chart-card"
        title={<CardTitle icon={<ThunderboltOutlined />} color="#8B5CF6">Pipeline Flow</CardTitle>}
        extra={
          <button
            type="button"
            className="dashboard-flow-open-board"
            onClick={() => navigate('/task-board')}
          >
            Open board <RightOutlined style={{ fontSize: 10 }} />
          </button>
        }
        style={{ borderRadius: 16 }}
      >
        <p className="dashboard-flow-hint">
          ✨ One owner · one requirement per gate — advance when ready.
        </p>
        <div className="dashboard-flow-track">
          {pipelineFlow.map((p, i) => (
            <React.Fragment key={p.phase}>
              <button
                type="button"
                className="dashboard-flow-card"
                style={{ borderTopColor: getPhaseColor(p.phase) }}
                onClick={() => navigate('/task-board')}
              >
                <span className="dashboard-flow-card-head">
                  <span className="dashboard-flow-gate" style={{ color: getPhaseColor(p.phase) }}>
                    Gate {getGateLetter(p.phase)}
                  </span>
                  <span className="dashboard-flow-count">{p.count}</span>
                </span>
                <span className="dashboard-flow-name">{getPhaseLabel(p.phase)}</span>
                <span className="dashboard-flow-owner">
                  <NameAvatar name={getPhaseOwnerRole(p.phase)} size={20} />
                  <span className="dashboard-flow-owner-name">{getPhaseOwnerRole(p.phase).split(' / ')[0]}</span>
                </span>
              </button>
              {i < pipelineFlow.length - 1 && (
                <RightOutlined className="dashboard-flow-arrow" />
              )}
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Activity & Team */}
      <Row gutter={[20, 20]} className="dashboard-chart-row">
        <Col xs={24} lg={14}>
          <Card
            className="dashboard-chart-card"
            title={<CardTitle icon={<WarningOutlined />} color="#DC2626">Needs Attention</CardTitle>}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {phaseOverdueCount} phase · {submissionOverdueCount} submission overdue
              </Text>
            }
            style={{ borderRadius: 16, height: '100%' }}
          >
            {attentionCards.length === 0 ? (
              <div className="dashboard-widget-empty">Nothing urgent — you're all caught up. 🎉</div>
            ) : (
              <div className="dashboard-attention-list">
                {attentionCards.map((card) => {
                  const severity = cardSeverity(card);
                  return (
                    <button
                      key={card.opportunityId}
                      type="button"
                      className="dashboard-attention-item"
                      onClick={() => navigate(`/opportunities/${card.opportunityId}`)}
                    >
                      <span
                        className="dashboard-attention-dot"
                        style={{ background: severity === 'overdue' ? 'var(--color-error)' : 'var(--color-warning)' }}
                      />
                      <span className="dashboard-attention-main">
                        <span className="dashboard-attention-title">{card.title}</span>
                        <span className="dashboard-attention-agency">{card.agencyName}</span>
                      </span>
                      <span
                        className="dashboard-attention-gate"
                        style={{
                          background: `${getPhaseColor(card.currentPhase)}1f`,
                          color: getPhaseColor(card.currentPhase),
                        }}
                      >
                        {getPhaseShortLabel(card.currentPhase)}
                      </span>
                      <span
                        className="dashboard-attention-due"
                        style={{ color: severity === 'overdue' ? 'var(--color-error)' : 'var(--color-warning)' }}
                      >
                        {cardDueText(card)}
                      </span>
                      <RightOutlined className="dashboard-attention-arrow" />
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            className="dashboard-chart-card"
            title={<CardTitle icon={<HistoryOutlined />} color="#6366F1">Recent Activity</CardTitle>}
            style={{ borderRadius: 16, height: '100%' }}
          >
            {activityError ? (
              <div className="dashboard-widget-empty">Couldn't load activity.</div>
            ) : activity.length === 0 ? (
              <div className="dashboard-widget-empty">No recent activity yet.</div>
            ) : (
              <div className="dashboard-activity-list">
                {activity.slice(0, 6).map((event) => {
                  const meta = ACTION_META[event.action] || {
                    label: String(event.action || '').replace(/_/g, ' '),
                    color: '#94a3b8',
                  };
                  return (
                    <div key={event.id} className="dashboard-activity-item">
                      <NameAvatar name={event.username} />
                      <div className="dashboard-activity-body">
                        <div className="dashboard-activity-text">
                          <span className="dashboard-activity-user">{firstNameOf(event.username)}</span>{' '}
                          <span className="dashboard-activity-verb">{describeActivity(event)}</span>
                        </div>
                        <div className="dashboard-activity-meta">
                          <span
                            className="dashboard-activity-chip"
                            style={{ background: `${meta.color}1f`, color: meta.color }}
                          >
                            {meta.label}
                          </span>
                          <span className="dashboard-activity-time">{formatRelativeTime(event.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]} className="dashboard-chart-row">
        <Col xs={24} md={12}>
          <Card
            className="dashboard-chart-card"
            title={<CardTitle icon={<TeamOutlined />} color="#0284C7">Team Workload</CardTitle>}
            style={{ borderRadius: 16, height: '100%' }}
          >
            {actionItemsError ? (
              <div className="dashboard-widget-empty">Couldn't load workload.</div>
            ) : workload.length === 0 ? (
              <div className="dashboard-widget-empty">No assignments yet.</div>
            ) : (
              <div className="dashboard-workload-list">
                {workload.map((w) => (
                  <div key={w.name} className="dashboard-workload-item">
                    <NameAvatar name={w.name} size={30} />
                    <div className="dashboard-workload-body">
                      <div className="dashboard-workload-row">
                        <span className="dashboard-workload-name">{w.name}</span>
                        <Text type="secondary" style={{ fontSize: 12 }}>{w.count} active</Text>
                      </div>
                      <div className="dashboard-workload-track">
                        <div
                          className="dashboard-workload-bar"
                          style={{ width: `${(w.count / maxLoad) * 100}%`, backgroundColor: colorFor(w.name) }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            className="dashboard-chart-card"
            title={<CardTitle icon={<ClockCircleOutlined />} color="#D97706">Upcoming Deadlines</CardTitle>}
            style={{ borderRadius: 16, height: '100%' }}
          >
            {upcomingCards.length === 0 ? (
              <div className="dashboard-widget-empty">No upcoming deadlines.</div>
            ) : (
              <div className="dashboard-upcoming-list">
                {upcomingCards.map((card) => {
                  const due = card.phaseDeadline || card.dueDate;
                  const severity = cardSeverity(card);
                  return (
                    <button
                      key={card.opportunityId}
                      type="button"
                      className="dashboard-upcoming-item"
                      onClick={() => navigate(`/opportunities/${card.opportunityId}`)}
                    >
                      <span className="dashboard-upcoming-date">
                        <span className="dashboard-upcoming-day">{dayjs(due).format('D')}</span>
                        <span className="dashboard-upcoming-month">{dayjs(due).format('MMM')}</span>
                      </span>
                      <span
                        className="dashboard-upcoming-phase-dot"
                        style={{ background: getPhaseColor(card.currentPhase) }}
                      />
                      <span className="dashboard-upcoming-main">
                        <span className="dashboard-upcoming-title">{card.title}</span>
                        <span className="dashboard-upcoming-gate">{getPhaseShortLabel(card.currentPhase)}</span>
                      </span>
                      <span
                        className="dashboard-upcoming-due"
                        style={{
                          color: severity === 'overdue'
                            ? 'var(--color-error)'
                            : severity === 'soon'
                              ? 'var(--color-warning)'
                              : 'var(--color-text-secondary)',
                        }}
                      >
                        {cardDueText(card) || 'scheduled'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
      </Row>
      </div>
    </div>
  );
};

export default DashboardPage;
